import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { canManageAircraftStatus } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Wrench, AlertTriangle, CheckCircle2, Activity, FileWarning, XCircle } from "lucide-react";
import { toast } from "sonner";

const fleetOrder = ["AVRO RJ85", "AVRO RJ100", "DASH 8", "ATR72", "DORNIER 228", "SKYVAN"];
const REFRESH_MS = 30_000;  // poll active-work every 30s

const STATUS_CLASSES = {
  normal: "bg-emerald-50 border-emerald-200 text-emerald-800",
  heavy_check: "bg-blue-50 border-blue-200 text-blue-800",
  mel: "bg-amber-50 border-amber-200 text-amber-800",
  capex: "bg-rose-50 border-rose-200 text-rose-800",
  unserviceable: "bg-red-50 border-red-300 text-red-800",
  offline: "bg-gray-100 border-gray-300 text-gray-600",
};

export default function AircraftStatus() {
  const { user } = useAuth();
  const canEdit = canManageAircraftStatus(user);
  const [aircraft, setAircraft] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [busy, setBusy] = useState(null); // tail currently mutating
  const [activeWork, setActiveWork] = useState({});

  const refresh = () => api.get("/aircraft").then((r) => setAircraft(r.data));
  const refreshActive = React.useCallback(
    () => api.get("/aircraft/active-work").then((r) => setActiveWork(r.data || {})).catch(() => {}),
    [],
  );
  useEffect(() => {
    refresh();
    api.get("/aircraft-statuses").then((r) => setStatuses(r.data));
    refreshActive();
    const iv = setInterval(refreshActive, REFRESH_MS);
    return () => clearInterval(iv);
  }, [refreshActive]);

  const grouped = useMemo(() => {
    const g = {};
    aircraft.forEach((a) => { (g[a.fleet] = g[a.fleet] || []).push(a); });
    return g;
  }, [aircraft]);
  const orderedFleets = useMemo(() => [
    ...fleetOrder,
    ...Object.keys(grouped).filter((f) => !fleetOrder.includes(f)),
  ].filter((f) => (grouped[f] || []).length > 0), [grouped]);

  const setStatus = async (tail, status) => {
    setBusy(tail);
    try {
      await api.patch(`/aircraft/${encodeURIComponent(tail)}/status`, { status });
      toast.success(`${tail} · ${statusLabel(status, statuses)}`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update status");
    } finally {
      setBusy(null);
    }
  };

  const counts = useMemo(() => {
    const c = { normal: 0, heavy_check: 0, mel: 0, capex: 0, unserviceable: 0 };
    aircraft.forEach((a) => { c[a.status || "normal"] = (c[a.status || "normal"] || 0) + 1; });
    return c;
  }, [aircraft]);
  const totalActive = useMemo(
    () => Object.values(activeWork).reduce((n, arr) => n + (arr?.length || 0), 0),
    [activeWork],
  );

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-baseline justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Fleet Status · MST</div>
          <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight mt-1">Aircraft status</h1>
          <p className="mt-2 text-sm text-gray-600 max-w-2xl">
            When a tail is on <span className="font-medium">Heavy Check</span> or <span className="font-medium">Down for CAPEX</span>, engineers must pick a CAPEX Account Code before clocking in — the selected code lands in column AV of the exported timesheet.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {totalActive > 0 && (
            <StatusPill
              label="Active now"
              count={totalActive}
              klass="bg-emerald-50 border-emerald-200 text-emerald-800"
              icon={<span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />}
            />
          )}
          <StatusPill label="Serviceable" count={counts.normal} klass={STATUS_CLASSES.normal} icon={<CheckCircle2 className="w-3.5 h-3.5" />} />
          <StatusPill label="Heavy Check" count={counts.heavy_check} klass={STATUS_CLASSES.heavy_check} icon={<Wrench className="w-3.5 h-3.5" />} />
          <StatusPill label="MEL" count={counts.mel} klass={STATUS_CLASSES.mel} icon={<FileWarning className="w-3.5 h-3.5" />} />
          <StatusPill label="CAPEX" count={counts.capex} klass={STATUS_CLASSES.capex} icon={<AlertTriangle className="w-3.5 h-3.5" />} />
          <StatusPill label="Unserviceable" count={counts.unserviceable} klass={STATUS_CLASSES.unserviceable} icon={<XCircle className="w-3.5 h-3.5" />} />
        </div>
      </div>

      {!canEdit && (
        <div className="mt-6 p-3 border border-summit-hairline bg-summit-glacier-soft rounded-md text-xs text-gray-600" data-testid="read-only-banner">
          Read-only view. Only admins, crew chiefs, and managers can change aircraft status.
        </div>
      )}

      <div className="mt-8 space-y-8">
        {orderedFleets.map((fleet) => (
          <div key={fleet} data-testid={`fleet-block-${fleet.replace(/\s+/g, "-").toLowerCase()}`}>
            <div className="flex items-center gap-2 mb-3">
              <h2 className="font-display text-sm uppercase tracking-widest text-gray-600">{fleet}</h2>
              <div className="h-px bg-gray-200 flex-1" />
              <span className="font-mono text-[10px] text-gray-400">
                {(grouped[fleet] || []).length} tail{(grouped[fleet] || []).length === 1 ? "" : "s"}
              </span>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {grouped[fleet].map((a) => (
                <AircraftRow
                  key={a.tail}
                  aircraft={a}
                  statuses={statuses}
                  canEdit={canEdit}
                  busy={busy === a.tail}
                  onChange={(s) => setStatus(a.tail, s)}
                  activeSessions={activeWork[a.tail] || []}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function AircraftRow({ aircraft, statuses, canEdit, busy, onChange, activeSessions }) {
  const status = aircraft.status || "normal";
  const label = statusLabel(status, statuses);
  const klass = STATUS_CLASSES[status] || STATUS_CLASSES.normal;
  const updatedBy = aircraft.status_updated_by_name;
  const updatedAt = aircraft.status_updated_at
    ? new Date(aircraft.status_updated_at).toLocaleString()
    : null;
  const active = activeSessions || [];

  return (
    <div
      className={`border rounded-md p-4 transition-colors ${klass}`}
      data-testid={`aircraft-status-card-${aircraft.tail}`}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <div>
          <div className="font-mono text-base font-medium tracking-tight text-summit-ink">{aircraft.tail}</div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">{aircraft.fleet}</div>
        </div>
        <span
          className={`px-2 py-0.5 rounded-full border font-mono text-[10px] uppercase tracking-widest ${klass}`}
          data-testid={`aircraft-status-pill-${aircraft.tail}`}
        >
          {label}
        </span>
      </div>

      {/* Active-work indicator — appears only when someone is currently
          clocked in on this tail. Green pulse dot + count + name list. */}
      {active.length > 0 && (
        <div
          className="mb-2 flex flex-col gap-1 rounded border border-emerald-200 bg-emerald-50/70 px-2 py-1.5"
          data-testid={`active-work-block-${aircraft.tail}`}
        >
          <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-emerald-800">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            <Activity className="w-3 h-3" />
            <span data-testid={`active-work-count-${aircraft.tail}`}>
              {active.length} active
            </span>
          </div>
          <ul className="pl-3 space-y-0.5 font-mono text-[10.5px] leading-tight text-emerald-900">
            {active.map((s) => {
              const badges = [];
              if (s.is_work_on_stat) badges.push("Stat");
              if (s.is_ot) badges.push("OT");
              if (s.is_acting_crew_chief) badges.push("ACC");
              return (
                <li key={s.user_id} title={s.capex_code_name || ""}>
                  <span>{s.user_display}</span>
                  {badges.length > 0 && (
                    <span className="ml-1 opacity-70">· {badges.join(" · ")}</span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {canEdit ? (
        <Select value={status} onValueChange={onChange} disabled={busy}>
          <SelectTrigger className="h-8 text-xs bg-white" data-testid={`aircraft-status-select-${aircraft.tail}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {statuses.map((s) => (
              <SelectItem key={s.code} value={s.code} data-testid={`aircraft-status-option-${aircraft.tail}-${s.code}`}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <div className="font-mono text-[10px] text-gray-500">Read-only</div>
      )}
      {(updatedBy || updatedAt) && (
        <div className="mt-2 font-mono text-[10px] text-gray-500 truncate" title={`${updatedBy || ""} · ${updatedAt || ""}`}>
          {updatedBy && <>Updated by <span className="text-summit-primary-soft">{updatedBy}</span></>}
          {updatedAt && <> · {updatedAt}</>}
        </div>
      )}
    </div>
  );
}

function statusLabel(code, statuses) {
  return (statuses.find((s) => s.code === code) || {}).label || code;
}

function StatusPill({ label, count, klass, icon }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border font-mono text-[10px] uppercase tracking-widest ${klass}`}
      data-testid={`fleet-count-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      {icon}
      <span className="tabular-nums">{count}</span>
      <span>{label}</span>
    </span>
  );
}
